import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SchemaRegion } from "../../../../api/restaurantSchema";
import { CheckIcon, PersonIcon } from "../../../../components/restaurant-schema/schemaIcons";
import { formatClock } from "../../logic/dayTime";
import type { PickTable } from "../../logic/newReservation";
import { canTogglePick, pickStateOf, type PickState } from "../../logic/tablePick";
import type { NewReservationForm } from "../../hooks/useNewReservationForm";
import { pickContextOf } from "../../hooks/useTablePicker";
import { useReservationFormat } from "../../hooks/useReservationFormat";
import RegionGrid from "../RegionGrid";
import { SectionLabel, SuggestTableButton } from "./formPrimitives";

// Плочката на маса по състоянието ѝ: свободните са в обикновен цвят, оцветена е само
// избраната; заетата е щрихована като на схемата (tableTones.ts).
const TILE_TONE: Record<PickState, string> = {
  selected: "border-2 border-brand bg-brand-tint",
  fits: "border border-shade-3 bg-surface",
  free: "border border-shade-3 bg-surface",
  busy: "border border-schema-blocked-edge bg-[repeating-linear-gradient(135deg,var(--color-schema-blocked)_0_5px,var(--color-schema-blocked-stripe)_5px_10px)]",
  unknown: "border border-shade-2 bg-surface opacity-60",
};

const NAME_TONE: Record<PickState, string> = {
  selected: "text-brand-deep-ink",
  fits: "text-ink",
  free: "text-ink",
  busy: "text-schema-blocked-ink",
  unknown: "text-muted",
};

const SEATS_TONE: Record<PickState, string> = {
  selected: "text-brand-deep-ink",
  fits: "text-body",
  free: "text-body",
  busy: "text-schema-blocked-ink",
  unknown: "text-muted",
};

// Номер („12") — едър; име („Тераса 12") — по-дребно и на до 2 реда, за да не се реже.
const nameSize = (name: string) => (name.length <= 4 ? "truncate text-[18px]" : "line-clamp-2 break-words text-[14px]");

// По подразбиране се виждат само масите, които стават (и избраните).
const isOffered = (state: PickState) => state === "selected" || state === "fits";

/** Естествено сравнение на имената („2" < „10"). */
const byName = (left: PickTable, right: PickTable) => left.name.localeCompare(right.name, undefined, { numeric: true });

/**
 * Изборът на маса на ТЕЛЕФОН (там схемата не се показва): „РЕГИОН" —
 * решетка по 3 (всички видими) с броя свободни за гостите маси; „МАСИ *" с
 * „☆ Предложи маса",
 * ред „Свободни маси за 2 гости · 19:00–21:00" и плочки с масите на
 * региона — първо само подходящите; „Покажи всички маси (N)" добавя
 * заетите (щриховани, некликаеми) и тези с по-малко места. Регионът е
 * общият на страницата (`view.region`) — „Предложи маса" превключва на него.
 */
const MobileTablePicker = ({
  form,
  regions,
  activeRegionId,
  onRegionChange,
  onSuggest,
}: {
  form: NewReservationForm;
  regions: SchemaRegion[];
  activeRegionId: number;
  onRegionChange: (regionId: number) => void;
  // „☆ Предложи маса" (избира и превключва на региона ѝ).
  onSuggest: () => void;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const [showAll, setShowAll] = useState(false);

  const context = pickContextOf(form);
  const withState = (table: PickTable) => ({ table, state: pickStateOf(table, context) });
  const regionTables = (regionId: number) =>
    form.tables
      .filter((table) => table.regionId === regionId)
      .sort(byName)
      .map(withState);

  const tables = regionTables(activeRegionId);
  // Докато заетостта не е ясна — всички (изключени), иначе списъкът е празен.
  const offered = form.isDayUnknown || showAll ? tables : tables.filter(({ state }) => isOffered(state));
  const hiddenCount = tables.length - offered.length;
  const activeRegion = regions.find((region) => region.id === activeRegionId);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t("reservations.form.region")}</SectionLabel>
        <RegionGrid
          regions={regions}
          activeId={activeRegionId}
          onChange={(regionId) => regionId !== null && onRegionChange(regionId)}
          captionOf={(region) =>
            form.isDayUnknown
              ? "…"
              : t("reservations.form.freeCount", {
                  count: regionTables(region.id).filter(({ state }) => state === "fits").length,
                })
          }
          markOf={(region) => regionTables(region.id).some(({ state }) => state === "selected")}
          markLabel={t("reservations.form.regionHasSelected")}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <SectionLabel required>{t("reservations.form.tables")}</SectionLabel>
          <SuggestTableButton onClick={onSuggest} tall />
        </div>

        {!form.isDayUnknown && activeRegion && (
          <div className="text-[12.5px] font-medium leading-[1.45] text-body">
            {t("reservations.form.freeInRegion", {
              guests: format.guests(form.draft.guests),
              region: activeRegion.name,
              time: `${formatClock(form.fromMs)}–${formatClock(form.toMs)}`,
            })}
          </div>
        )}

        {offered.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {offered.map(({ table, state }) => {
              const enabled = canTogglePick(state);
              return (
                <button
                  key={table.id}
                  type="button"
                  disabled={!enabled}
                  aria-pressed={state === "selected"}
                  title={format.tableName(table.name)}
                  onClick={() => form.toggleTable(table.id)}
                  className={`relative flex min-h-[68px] min-w-0 cursor-pointer flex-col justify-between gap-1 rounded-[10px] px-3 py-2.5 text-left transition-colors duration-150 disabled:cursor-not-allowed ${TILE_TONE[state]}`}
                >
                  <span
                    className={`pr-5 font-bold leading-[1.2] tabular-nums ${nameSize(table.name)} ${NAME_TONE[state]}`}
                  >
                    {table.name}
                  </span>
                  <span
                    className={`flex items-center gap-1 text-[12px] font-semibold leading-[1.2] ${SEATS_TONE[state]}`}
                  >
                    <PersonIcon className="size-3 shrink-0" />
                    <span className="truncate">
                      {state === "busy" ? t("reservations.picker.badgeBusy") : format.seats(table.seats)}
                    </span>
                  </span>
                  {state === "selected" && (
                    <span className="absolute right-2 top-2 flex size-5 items-center justify-center rounded-full bg-brand text-white">
                      <CheckIcon className="size-3" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-[10px] border border-dashed border-shade-4 px-3.5 py-3 text-center text-[12.5px] font-medium text-body">
            {tables.length === 0
              ? t("reservations.emptyRegion")
              : t("reservations.form.noFreeInRegion", { guests: format.guests(form.draft.guests) })}
          </div>
        )}

        {!form.isDayUnknown && (hiddenCount > 0 || showAll) && (
          <button
            type="button"
            onClick={() => setShowAll((current) => !current)}
            className="h-10 w-full cursor-pointer rounded-[10px] border border-shade-3 bg-surface px-3.5 text-[13.5px] font-semibold text-ink transition-colors duration-150 hover:bg-fill"
          >
            {showAll
              ? t("reservations.form.showOfferedOnly")
              : t("reservations.form.showAllTables", { count: hiddenCount })}
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileTablePicker;
